/* eslint-disable no-undef */
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { faEnvelope } from '@fortawesome/pro-regular-svg-icons/faEnvelope';
import { faHomeLgAlt } from '@fortawesome/pro-regular-svg-icons/faHomeLgAlt';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons/faInfoCircle';
import { faPaperPlaneTop } from '@fortawesome/pro-regular-svg-icons/faPaperPlaneTop';
import { faPhone } from '@fortawesome/pro-regular-svg-icons/faPhone';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons/faExclamationCircle';
import { trackEnquiryClick, trackGaEvent } from '@propertypal/shared/src/services/analytics';
import { Property } from '@propertypal/shared/src/types/property';
import getContact from '@propertypal/shared/src/utils/enquiries/getContact';
import { getAgentLogos } from '@propertypal/shared/src/utils/property';
import getSellingAgent, { getAgentColours } from '@propertypal/shared/src/utils/property/getSellingAgent';
import Link from 'next/link';
import React, { CSSProperties, FunctionComponent, useState } from 'react';
import { useTheme } from 'styled-components';
import Button from '../buttons/Button';
import HorizontalDivider from '../divider/HorizontalDivider';
import Chevron from '../icons/Chevron';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import { RowWrap } from '../layout';
import { Text, LinkText, SubHeading } from '../typography';
import {
  Title,
  AgentBox,
  AgentLogo,
  AgentText,
  LicenceWarning,
  AgentBoxHeader,
  AgentBoxContent,
  AgentExtraInfo,
  AgentListItem,
  IconBox,
  ShowMoreDetails,
  AgentButtons,
  Divider,
} from './PropertyAgentBox.style.pn';
import PropertyAgentBoxFixed from './PropertyAgentBoxFixed';
import PropertyPhoneNumberButton from './PropertyPhoneNumberButton';

export interface PropertyAgentBoxProps {
  containerStyle?: CSSProperties;
  property: Property;
  onCallClick: (location: string) => void;
  onShowNumberClick: (location: string, accountNumber?: string) => void;
  onEnquiryClick: (accountNumber?: string) => void;
  onRegisterInterestClick: () => void;
  registerInterest?: boolean;
  leadId: number;
  revealNumber?: string | null;
}

const PropertyAgentBox: FunctionComponent<PropertyAgentBoxProps> = (props) => {
  const { property } = props;
  const theme = useTheme();
  const sellingAgent = getSellingAgent(property);
  const {
    pdpBrandingBgColour,
    pdpBrandingBorderColour,
    pdpBrandingButtonBgColour,
    pdpBrandingButtonTextColour,
    pdpBrandingLinkColour,
  } = getAgentColours(property.enhancedBranding, sellingAgent);
  const [showMore, setShowMore] = useState(false);
  const accountContact = property.contacts?.all.find((c) => c.accountNumber === property.account.accountNumber);
  // const agentAward = sellingAgent?.propertypalAwards?.length ? sellingAgent?.propertypalAwards[0] : null;
  const logos = getAgentLogos(props.property);
  const contactInfo = getContact(property);
  const page = property.type === 'development' ? 'DDP' : 'PDP';
  const leadsLink = property.leadIds?.length
    ? `/my/enquiries?leadId=${property.leadIds[0]}`
    : props.leadId
    ? `/my/enquiries?leadId=${props.leadId}`
    : '';

  const enquireNowClick = (location: string, accountNumber?: string) => {
    trackEnquiryClick(page, location, property);
    props.onEnquiryClick(accountNumber);
  };

  const registerInterestClick = (location: string) => {
    trackGaEvent('register_interest_click', { page, location });
    props.onRegisterInterestClick();
  };

  return (
    <>
      <AgentBox color={pdpBrandingBorderColour} data-testid="agent-box">
        {property.developerAsPrimaryBrand && (
          <>
            <AgentBoxHeader>
              <SubHeading align="center" fontSize={20} mb={15}>
                Developed By
              </SubHeading>
              <AgentLogo data-testid="agent-logo" src={property.account.logo?.standard} alt="Agent Logo" />
            </AgentBoxHeader>

            {(accountContact?.email || accountContact?.phone) && (
              <AgentBoxContent>
                <div>
                  {accountContact?.email && !leadsLink && (
                    <Button
                      containerStyle={{ height: 50, marginBottom: 15 }}
                      onClick={() => enquireNowClick('Enquiry Box', property.account.accountNumber)}
                      testID="enquireNowButton"
                      type="button"
                      color={pdpBrandingButtonBgColour}
                      fontColor={pdpBrandingButtonTextColour}
                      icon={faEnvelope}
                    >
                      Email the Developer
                    </Button>
                  )}

                  {accountContact?.phone && (
                    <PropertyPhoneNumberButton
                      {...props}
                      containerStyle={{ height: 50, margin: '0 auto 15px' }}
                      location="Enquiry Box"
                      accountNumber={property.account.accountNumber}
                      useShortText={false}
                      suffix="Developer"
                      icon={faPhone}
                    />
                  )}
                </div>

                <Divider />
              </AgentBoxContent>
            )}
          </>
        )}

        {!property.developerAsPrimaryBrand && sellingAgent && (
          <AgentBoxHeader color={pdpBrandingBgColour}>
            <Link href={sellingAgent.landingPageUrl} prefetch={false}>
              {sellingAgent.logo && sellingAgent.logo.standard && (
                <AgentLogo data-testid="agent-logo" src={sellingAgent.logo.standard} alt="Agent Logo" />
              )}
            </Link>
          </AgentBoxHeader>
        )}

        <Title>Marketed by {sellingAgent ? sellingAgent.organisation : 'multiple agents'}</Title>

        {property.developerAsPrimaryBrand && (
          <RowWrap gap={15} mb={15} style={{ justifyContent: 'center' }}>
            {logos.map((logo) => (
              <AgentLogo
                data-testid="agent-logo"
                key={logo.url}
                src={logo.url}
                alt="Agent Logo"
                style={{ maxWidth: `calc(${100 / logos.length}% - 15px)` }}
              />
            ))}
          </RowWrap>
        )}

        {sellingAgent && sellingAgent.psrLicenceNumber && property.countryCode === 'IRL' && (
          <AgentText>
            PSR Licence: {sellingAgent.psrLicenceNumber}
            {sellingAgent.psrLicenceNumber === 'NO LICENCE' && (
              <LicenceWarning href="/psr-licence-number" prefetch={false}>
                ?
              </LicenceWarning>
            )}
          </AgentText>
        )}

        <Text className="show-print" align="center" fontSize={18} mb={15}>
          {sellingAgent?.officePhone?.international}
        </Text>

        {!!property.leadIds?.length && (
          <Text
            align="center"
            color={theme.primary}
            fontSize={17}
            fontWeight="600"
            style={{ maxWidth: 240, margin: '0 auto' }}
          >
            <FontAwesomeIcon icon={faExclamationCircle} style={{ marginRight: 8 }} />
            You&apos;ve already made an enquiry about this property.{' '}
            <Link href={leadsLink} style={{ textDecoration: 'underline' }}>
              View <Chevron size={10} />
            </Link>
          </Text>
        )}

        <AgentBoxContent className="hide-print">
          <AgentButtons>
            {/* {sellingAgent?.enhancedBranding && agentAward && (
                <Link href={sellingAgent.landingPageUrl} prefetch={false}>
                  <AwardLogo src={agentAward.imageUrl} alt={agentAward.label} data-testid="award-logo" />
                </Link>
              )} */}

            {property.published && (
              <>
                {props.registerInterest && !leadsLink && (
                  <>
                    <Button
                      containerStyle={{ height: 50, marginBottom: 15, maxWidth: 400 }}
                      onClick={() => registerInterestClick('Enquiry Box')}
                      testID="registerInterestButton"
                      type="button"
                      color={pdpBrandingButtonBgColour}
                      fontColor={pdpBrandingButtonTextColour}
                      icon={faPaperPlaneTop}
                      iconColour={pdpBrandingButtonTextColour}
                    >
                      Register Your Interest
                    </Button>

                    <HorizontalDivider color={theme.textDark} dividerColor="transparent" text="or" mb={15} />
                  </>
                )}

                {contactInfo.phone && (
                  <PropertyPhoneNumberButton
                    {...props}
                    containerStyle={{
                      height: 50,
                      margin: '0 auto 15px',
                      maxWidth: 400,
                      border: !property.featuredDevelopmentContent ? 'solid 1px #919191' : undefined,
                    }}
                    location="Enquiry Box"
                    useShortText={false}
                    icon={faPhone}
                    revealNumber={props.revealNumber}
                  />
                )}

                {!!leadsLink && (
                  <Link href={leadsLink} style={{ width: '100%', maxWidth: 400, marginBottom: 15 }}>
                    <Button
                      containerStyle={{ height: 50 }}
                      type="button"
                      color={pdpBrandingButtonBgColour}
                      fontColor={pdpBrandingButtonTextColour}
                      icon={faPaperPlaneTop}
                      iconColour={pdpBrandingButtonTextColour}
                    >
                      Send another message
                    </Button>
                  </Link>
                )}

                {contactInfo.email && !leadsLink && (
                  <Button
                    containerStyle={{ height: 50, marginBottom: 15, maxWidth: 400 }}
                    onClick={() => enquireNowClick('Enquiry Box')}
                    testID="enquireNowButton"
                    type="button"
                    color={pdpBrandingButtonBgColour}
                    fontColor={pdpBrandingButtonTextColour}
                    icon={faEnvelope}
                    iconColour={pdpBrandingButtonTextColour}
                  >
                    Message Agent
                  </Button>
                )}
              </>
            )}
          </AgentButtons>

          {sellingAgent?.enhancedBranding && (
            <AgentExtraInfo show={showMore}>
              <AgentListItem>
                <IconBox>
                  <FontAwesomeIcon
                    icon={faInfoCircle}
                    color={pdpBrandingLinkColour || theme.textDark}
                    style={{ fontSize: 18 }}
                  />
                </IconBox>

                <Text>
                  <Link href={sellingAgent.landingPageUrl}>
                    <LinkText as="span" fontSize={17} color={pdpBrandingLinkColour} className="pp-primary-text">
                      More information about us <Chevron size={11} />
                    </LinkText>
                  </Link>
                </Text>
              </AgentListItem>

              {sellingAgent.searchUrl && (
                <AgentListItem>
                  <IconBox>
                    <FontAwesomeIcon
                      icon={faHomeLgAlt}
                      color={pdpBrandingLinkColour || theme.textDark}
                      style={{ fontSize: 18 }}
                    />
                  </IconBox>

                  <Text>
                    <Link href={sellingAgent.searchUrl} prefetch={false}>
                      <LinkText as="span" fontSize={17} color={pdpBrandingLinkColour}>
                        View our similar properties <Chevron size={11} />
                      </LinkText>
                    </Link>
                  </Text>
                </AgentListItem>
              )}

              {/* {agentAward && sellingAgent.landingPageUrl && (
                  <AgentListItem>
                    <IconBox>
                      <FontAwesomeIcon icon={faTrophy} color={theme.textDark} style={{ fontSize: 18 }} />
                    </IconBox>

                    <Link href={sellingAgent.landingPageUrl} prefetch={false}>
                      <AwardLogo src={agentAward.imageUrl} alt={agentAward.label} data-testid="award-logo" />
                    </Link>
                  </AgentListItem>
                )} */}
            </AgentExtraInfo>
          )}
        </AgentBoxContent>

        {sellingAgent?.enhancedBranding && (
          <ShowMoreDetails
            color={pdpBrandingLinkColour}
            onClick={() => setShowMore(true)}
            show={!showMore}
            className="hide-print"
          >
            Show more details <FontAwesomeIcon icon={faChevronDown} color={pdpBrandingLinkColour} />
          </ShowMoreDetails>
        )}
      </AgentBox>

      <PropertyAgentBoxFixed
        registerInterestClick={registerInterestClick}
        enquireNowClick={enquireNowClick}
        contactInfo={contactInfo}
        leadsLink={leadsLink}
        sellingAgent={sellingAgent}
        {...props}
      />
    </>
  );
};

export default PropertyAgentBox;
